import { toast } from "react-toastify";
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Autocomplete,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { message, Popconfirm } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PackageService } from '../../../api/packageService';
import { PackageCountryPrice, Country } from '../../../@type/package.types';
import MuiTable from '../../../components/common/MuiTable';
import { Column } from '../../../utils/constants';
import axiosInstance from '../../../axios/axios-instance';
import { formatNumber } from '../../../utils/utils';

interface PackagePriceManagementProps {
  packageId: number;
  globalPrice: number;
  globalCurrency: string;
}

const PackagePriceManagement: React.FC<PackagePriceManagementProps> = ({
  packageId,
  globalPrice,
  globalCurrency,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [countryPrices, setCountryPrices] = useState<PackageCountryPrice[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [priceValue, setPriceValue] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCountryPrices();
    loadCountries();
  }, [packageId]);

  const loadCountries = async () => {
    try {
      const response = await axiosInstance.get('admin/setting/countries');
      if (response?.data?.data) {
        setCountries(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadCountryPrices = async () => {
    setLoading(true);
    try {
      const response = await PackageService.getPackageCountryPrices(packageId);
      // Server returns: { success: true, code: "OK", data: [...prices] }
      if (response?.data?.data) {
        setCountryPrices(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load country prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyGlobalToAll = async () => {
    try {
      setSaving(true);
      await PackageService.copyGlobalToAllCountries(packageId);
      toast.success(t('packages.copyGlobalSuccess'));
      loadCountryPrices();
    } catch (error) {
      console.error('Failed to copy global prices:', error);
      toast.error(t('packages.copyGlobalError'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddPrice = () => {
    setSelectedCountry(null);
    setPriceValue('');
    setOpenAddDialog(true);
  };

  const handleSavePrice = async () => {
    if (!selectedCountry || !priceValue) {
      toast.warning(t('packages.pleaseSelectCountryAndPrice'));
      return;
    }

    try {
      setSaving(true);
      await PackageService.upsertCountryPrice(packageId, {
        country_id: selectedCountry.id,
        price: parseFloat(priceValue),
      });
      toast.success(t('packages.priceUpdatedSuccess'));
      setOpenAddDialog(false);
      loadCountryPrices();
    } catch (error) {
      console.error('Failed to save price:', error);
      toast.error(t('packages.priceUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrice = async (countryId: number) => {
    try {
      await PackageService.deleteCountryPrice(packageId, countryId);
      toast.success(t('packages.priceDeletedSuccess'));
      loadCountryPrices();
    } catch (error) {
      console.error('Failed to delete price:', error);
      toast.error(t('packages.priceDeleteError'));
    }
  };

  const handleEditPrice = (price: PackageCountryPrice) => {
    const country = countries.find(c => c.id === price.country_id);
    if (country) {
      setSelectedCountry(country);
      setPriceValue(price.price.toString());
      setOpenAddDialog(true);
    }
  };

  // Get available countries (not yet configured)
  const availableCountries = countries.filter(
    country => !countryPrices.find(p => p.country_id === country.id)
  );

  // Define table columns
  const header: Column[] = [
    {
      id: 'country_name',
      label: t('packages.country'),
      minWidth: 150,
      align: 'left',
      format: (value: any) => {
        return (
          <div className="font-semibold">
            {value}
          </div>
        );
      },
    },
    {
      id: 'price_with_currency',
      label: t('packages.price'),
      minWidth: 120,
      align: 'center',
      format: (value: any) => {
        return (
          <div className="font-bold">
            {value}
          </div>
        );
      },
    },
    {
      id: 'currency',
      label: t('packages.currency'),
      minWidth: 100,
      align: 'center',
      format: (value: any) => {
        return <Chip label={value} size="small" color="primary" />;
      },
    },
    {
      id: 'is_active',
      label: t('packages.status'),
      minWidth: 100,
      align: 'center',
      format: (value: any) => {
        return (
          <Chip
            label={value ? t('common.active') : t('common.inactive')}
            size="small"
            color={value ? 'success' : 'default'}
          />
        );
      },
    },
    {
      id: 'action',
      label: t('common.table.actions'),
      minWidth: 120,
      align: 'center',
      format: (priceObject: any) => {
        return (
          <Box display="flex" justifyContent="center" gap={1}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEditPrice(priceObject)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <Popconfirm
              title={t('packages.deletePriceConfirm')}
              description={t('packages.deletePriceWarning')}
              okText={t('btn.confirm')}
              cancelText={t('btn.cancel')}
              onConfirm={() => handleDeletePrice(priceObject.country_id)}
              okButtonProps={{
                style: { background: '#ba2156', border: 'none' },
              }}
            >
              <IconButton size="small" color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Popconfirm>
          </Box>
        );
      },
    },
  ];

  const tableData = countryPrices.map(price => {
    const country = countries.find(c => c.id === price.country_id);
    return {
      ...price,
      action: price,  // Pass the entire price object for actions
      country_name: country?.name || 'Unknown',
      country_code: country?.code || '',
      price_with_currency: `${formatNumber(price.price)} ${price.currency}`,
    };
  });

  return (
    <>
      <Box>
        {/* Header with actions */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <h3 className="text-lg font-semibold">{t('packages.countryPricing')}</h3>
            <p className="text-sm text-gray-600 mt-1">
              {t('packages.configuredCount', { count: countryPrices.length, total: countries.length })}
            </p>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={handleCopyGlobalToAll}
              disabled={saving}
              size="small"
            >
              {t('packages.copyGlobalToAll')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddPrice}
              style={{ background: '#ba2156' }}
            >
              {t('packages.addCountryPrice')}
            </Button>
          </Box>
        </Box>

        {/* Global price reference */}
        <Box bgcolor="blue.50" p={2} borderRadius={1} mb={3}>
          <Typography variant="body2" className="font-semibold">
            💡 {t('packages.globalPriceReference')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {t('packages.currentGlobalPrice')}: <strong>{globalPrice} {globalCurrency}</strong>
          </Typography>
        </Box>

        {/* Table */}
        <MuiTable header={header} data={tableData} isLoading={loading} />

        {/* Add/Edit Price Dialog */}
        <Dialog
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {selectedCountry
              ? t('packages.editCountryPrice')
              : t('packages.addCountryPrice')}
          </DialogTitle>
          <DialogContent>
            <Box py={2}>
              <Autocomplete
                options={selectedCountry ? [selectedCountry] : availableCountries}
                getOptionLabel={(option) => `${option.name} (${option.currency_code})`}
                value={selectedCountry}
                onChange={(_, newValue) => setSelectedCountry(newValue)}
                disabled={!!selectedCountry}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('packages.selectCountry')}
                    fullWidth
                    required
                  />
                )}
              />

              {selectedCountry && (
                <Box mt={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('packages.price')}
                    value={priceValue}
                    onChange={(e) => setPriceValue(e.target.value)}
                    required
                    InputProps={{
                      endAdornment: (
                        <Chip
                          label={selectedCountry.currency_code}
                          size="small"
                          color="primary"
                        />
                      ),
                    }}
                    helperText={t('packages.priceHelperText')}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddDialog(false)} disabled={saving}>
              {t('btn.cancel')}
            </Button>
            <Button
              onClick={handleSavePrice}
              variant="contained"
              color="primary"
              disabled={saving || !selectedCountry || !priceValue}
            >
              {saving ? <CircularProgress size={24} /> : t('btn.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default PackagePriceManagement;
